import React from "react";
import { motion } from "framer-motion";

/**
 * Whimsical SVG decorations for the storybook UI. Purely decorative, non-interactive.
 */

export const Star = ({ className = "", size = 24, color = "#FDE68A", stroke = "#F59E0B" }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden>
        <path
            d="M12 2l2.6 6.4L21 9.2l-4.8 4.2L17.6 20 12 16.6 6.4 20l1.4-6.6L3 9.2l6.4-.8L12 2z"
            fill={color}
            stroke={stroke}
            strokeWidth="1.4"
            strokeLinejoin="round"
        />
    </svg>
);

export const Sparkle = ({ className = "", size = 20, color = "#FF6B9D" }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden>
        <path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M19 5l-4 4M9 15l-4 4"
              stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export const Cloud = ({ className = "", size = 80, color = "#FFFFFF", stroke = "#DDD6FE" }) => (
    <svg viewBox="0 0 100 60" width={size} height={size * 0.6} className={className} aria-hidden>
        <path
            d="M25 45 Q10 45 10 30 Q10 18 24 18 Q28 8 40 8 Q54 8 58 20 Q76 18 80 32 Q92 34 88 46 Q84 52 74 50 L28 50 Q22 50 25 45 Z"
            fill={color}
            stroke={stroke}
            strokeWidth="2"
            strokeLinejoin="round"
        />
    </svg>
);

export const Rainbow = ({ className = "", size = 120 }) => (
    <svg viewBox="0 0 120 60" width={size} height={size * 0.5} className={className} aria-hidden>
        <path d="M10 55 A50 50 0 0 1 110 55" fill="none" stroke="#FDA4AF" strokeWidth="6" strokeLinecap="round" />
        <path d="M18 55 A42 42 0 0 1 102 55" fill="none" stroke="#FDE68A" strokeWidth="6" strokeLinecap="round" />
        <path d="M26 55 A34 34 0 0 1 94 55" fill="none" stroke="#86EFAC" strokeWidth="6" strokeLinecap="round" />
        <path d="M34 55 A26 26 0 0 1 86 55" fill="none" stroke="#7DD3FC" strokeWidth="6" strokeLinecap="round" />
        <path d="M42 55 A18 18 0 0 1 78 55" fill="none" stroke="#C4B5FD" strokeWidth="6" strokeLinecap="round" />
    </svg>
);

export const Unicorn = ({ className = "", size = 200 }) => (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} aria-hidden>
        {/* Body */}
        <ellipse cx="105" cy="130" rx="55" ry="38" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        {/* Legs */}
        <rect x="75" y="150" width="10" height="30" rx="4" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        <rect x="95" y="155" width="10" height="30" rx="4" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        <rect x="115" y="155" width="10" height="30" rx="4" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        <rect x="135" y="150" width="10" height="30" rx="4" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        {/* Head */}
        <circle cx="55" cy="95" r="30" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        {/* Neck */}
        <path d="M65 115 Q75 125 90 120 L95 105 Q80 100 70 105 Z" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="3" />
        {/* Ear */}
        <path d="M42 68 L48 55 L55 70 Z" fill="#FFFFFF" stroke="#DDD6FE" strokeWidth="2" />
        {/* Horn */}
        <path d="M60 62 L70 30 L74 62 Z" fill="#FDE68A" stroke="#F59E0B" strokeWidth="2" strokeLinejoin="round" />
        <path d="M62 55 L72 45" stroke="#F59E0B" strokeWidth="1.5" />
        <path d="M63 48 L72 40" stroke="#F59E0B" strokeWidth="1.5" />
        {/* Mane — rainbow */}
        <path d="M75 75 Q100 60 100 90" fill="none" stroke="#FDA4AF" strokeWidth="8" strokeLinecap="round" />
        <path d="M80 80 Q105 70 108 100" fill="none" stroke="#FDE68A" strokeWidth="8" strokeLinecap="round" />
        <path d="M85 88 Q112 82 115 110" fill="none" stroke="#86EFAC" strokeWidth="8" strokeLinecap="round" />
        <path d="M88 95 Q115 92 118 118" fill="none" stroke="#7DD3FC" strokeWidth="8" strokeLinecap="round" />
        <path d="M90 102 Q118 100 120 125" fill="none" stroke="#C4B5FD" strokeWidth="8" strokeLinecap="round" />
        {/* Eye */}
        <circle cx="48" cy="92" r="3" fill="#2D1B4E" />
        <circle cx="49" cy="91" r="1" fill="#FFF" />
        {/* Cheek blush */}
        <circle cx="38" cy="102" r="4" fill="#FDA4AF" opacity="0.7" />
        {/* Tail rainbow */}
        <path d="M158 130 Q180 115 175 145" fill="none" stroke="#FDA4AF" strokeWidth="8" strokeLinecap="round" />
        <path d="M158 135 Q182 125 180 150" fill="none" stroke="#86EFAC" strokeWidth="8" strokeLinecap="round" />
        <path d="M158 140 Q178 138 178 158" fill="none" stroke="#7DD3FC" strokeWidth="8" strokeLinecap="round" />
    </svg>
);

export const OpenBook = ({ className = "", size = 200 }) => (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} aria-hidden>
        <path d="M20 60 Q60 50 100 60 L100 160 Q60 150 20 160 Z" fill="#FFFBF5" stroke="#2D1B4E" strokeWidth="3" />
        <path d="M180 60 Q140 50 100 60 L100 160 Q140 150 180 160 Z" fill="#FFFBF5" stroke="#2D1B4E" strokeWidth="3" />
        <path d="M100 60 L100 160" stroke="#2D1B4E" strokeWidth="3" />
        <path d="M35 80 L85 80 M35 92 L85 92 M35 104 L75 104" stroke="#FDA4AF" strokeWidth="2" strokeLinecap="round" />
        <path d="M115 80 L165 80 M115 92 L165 92 M115 104 L155 104" stroke="#7DD3FC" strokeWidth="2" strokeLinecap="round" />
        <circle cx="60" cy="130" r="8" fill="#FDE68A" />
        <circle cx="140" cy="130" r="8" fill="#C4B5FD" />
        <path d="M100 60 Q95 40 80 40 Q100 25 100 60" fill="#FF6B9D" opacity="0.8" />
    </svg>
);

/** Meadow decoration — drifting clouds up in the golden sky, a dashed orbit
    around the sun, and seeds lifting off the hills. Purely decorative. */
export const FloatingDecor = () => (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Dashed orbit around the sun, top right */}
        <div className="absolute -top-24 -right-16 w-[300px] h-[300px] rounded-full border-[3px] border-dashed border-terracotta/25 animate-spin-slow" />

        {/* Clouds drifting across the sky */}
        <div className="absolute top-[8%] left-[8%] anim-drift">
            <Cloud size={120} color="rgba(255,255,255,0.9)" stroke="rgba(255,255,255,0.9)" />
        </div>
        <div className="absolute top-[16%] left-[46%] anim-drift" style={{ animationDirection: "reverse", animationDuration: "30s" }}>
            <Cloud size={90} color="rgba(255,255,255,0.65)" stroke="rgba(255,255,255,0.65)" />
        </div>

        {/* Seeds lifting off the meadow */}
        <div className="absolute bottom-6 left-[26%] w-2 h-2 rounded-full bg-white anim-seed" />
        <div className="absolute bottom-4 left-[58%] w-1.5 h-1.5 rounded-full bg-white anim-seed" style={{ animationDelay: "1.6s", animationDuration: "9s" }} />
        <div className="absolute bottom-10 left-[82%] w-2.5 h-2.5 rounded-full bg-white anim-seed" style={{ animationDelay: "3.2s", animationDuration: "7s" }} />

        {/* Two stars, kept sparse */}
        <motion.div animate={{ scale: [0.85, 1.15, 0.85], opacity: [0.5, 1, 0.5] }} transition={{ duration: 3, repeat: Infinity }}
            className="absolute top-[30%] left-[38%]"><Star size={20} /></motion.div>
        <motion.div animate={{ scale: [0.85, 1.2, 0.85] }} transition={{ duration: 2.6, repeat: Infinity, delay: 1 }}
            className="absolute top-[48%] right-[14%]"><Star size={16} color="#FDA4AF" stroke="#D9486F" /></motion.div>
    </div>
);

/** Confetti burst - fires 20 pieces radiating out from an origin */
export const Confetti = ({ trigger }) => {
    if (!trigger) return null;
    const colors = ["#FF6B9D", "#FDE68A", "#86EFAC", "#7DD3FC", "#C4B5FD", "#FDA4AF"];
    const pieces = Array.from({ length: 22 }).map((_, i) => {
        const angle = (i / 22) * Math.PI * 2;
        const dist = 80 + Math.random() * 60;
        const tx = Math.cos(angle) * dist;
        const ty = Math.sin(angle) * dist;
        return (
            <span
                key={`${trigger}-${i}`}
                className="confetti-piece"
                style={{
                    left: "50%", top: "50%",
                    background: colors[i % colors.length],
                    "--tx": `${tx}px`,
                    "--ty": `${ty}px`,
                    borderRadius: i % 3 === 0 ? "50%" : "2px",
                    animationDelay: `${i * 15}ms`,
                }}
            />
        );
    });
    return <div className="pointer-events-none absolute inset-0 z-50">{pieces}</div>;
};
